import React, { useState, useEffect } from 'react';
import { Table, TableCell, TableRow } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MaskedInput from 'react-text-mask';
import {
  ratePerHourMask,
  guaranteedHoursMask,
} from '../helpers/textFieldNumberMasks';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import formatCurrency from '../helpers/formatCurrency';
import OVERTIME_MULTIPLIER from './OVERTIME_MULTIPLIER';
import WarningIcon from '@material-ui/icons/Warning';
import Typography from '@material-ui/core/Typography';
import WeeklyRateTable from './NonUnionTermsOfEmploymentTable/WeeklyRateTable';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: 40,
    marginBottom: 40,
  },
  terms: {
    marginTop: '40px',
  },
  textFieldInputContainerWarning: {
    borderBottom: `2px solid #FFB649`,
    '&:before': {
      border: 'none !important',
    },
    '&:after': {
      border: 'none !important',
    },
  },
  cellTitle: {
    backgroundColor: '#f9fdff',
    color: '#30ABA3',
    fontSize: 14,
    fontWeight: 'bold',
    border: '2px solid #D6EEED',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
  },
  itemContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectItemContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  dollar: {
    width: '10%',
    fontSize: 14,
    marginTop: 11,
    color: '#696969',
    paddingLeft: 7,
    fontFamily: 'Roboto, Helvetica, Helvetica Neue, san-serif',
  },
  hoursPerDayCell: {
    backgroundColor: '#f9fdff',
    color: '#30ABA3',
    fontSize: 14,
    fontWeight: 'bold',
    border: '2px solid #D6EEED',
    borderTop: 'none',
    paddingTop: 14,
    paddingBottom: 14,
    paddingLeft: 8,
  },
  tableSpacing: {
    marginTop: 16,
    marginBottom: 20,
    border: '1px solid #30ABA3',
  },
  topSpacing: {
    marginTop: 20,
  },
  hoursCopy: {
    marginTop: 10,
    paddingRight: 7,
    color: '#525252',
    fontSize: 16,
    fontWeight: 'normal',
  },
  sixthDayCell: {
    borderTop: 'none',
    backgroundColor: '#f9fdff',
    color: '#30ABA3',
    fontSize: 14,
    fontWeight: 'bold',
    border: '2px solid #D6EEED',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
  },
  radioGroupCell: {
    borderTop: 'none',
    backgroundColor: '#FFFFFF',
    color: '#30ABA3',
    fontSize: 14,
    fontWeight: 'bold',
    border: '2px solid #D6EEED',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
  },
  warningIcon: {
    color: '#FFA800',
    position: 'relative',
    top: 4,
    right: 8,
  },
  warningLabel: {
    color: '#FFA800',
    fontSize: 14,
    textAlign: 'center',
  },
  errorIcon: {
    color: 'red',
    position: 'relative',
    top: 4,
    right: 8,
  },
  errorLabel: {
    color: 'red',
    fontSize: 14,
    textAlign: 'center',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  hoursPerDay: {
    backgroundColor: '#f9fdff',
    color: '#30ABA3',
    fontSize: 14,
    fontWeight: 'bold',
  },
  radioBtns: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginLeft: 30,
  },
  radio: {
    '&$checked': {
      color: '#30ABA3',
    },
  },
  checked: {
    color: '#30ABA3',
    '&, & + $label': {
      color: '#30ABA3 !important',
      fontSize: 16,
    },
  },
  label: {
    color: '#262626',
    fontSize: 16,
  },
  rightCellArea: {
    width: '41%',
    backgroundColor: '#FFFFFF',
    color: '#30ABA3',
    fontSize: 14,
    fontWeight: 'bold',
    border: '2px solid #D6EEED',
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 8,
  },
  fieldArea: {
    width: '43%',
    backgroundColor: '#FFFFFF!important',
  },
}));

const theme = createMuiTheme({
  overrides: {
    MuiInput: {
      input: {
        fontSize: 16,
        fontWeight: 'normal',
        textAlign: 'center',
        width: 196,
        color: '#525252',
      },
      underline: {
        width: 196,
        color: '#696969',
        '&:before': {
          borderBottom: `1px solid #8D8D8D`,
        },
        '&$focused:not($disabled):before': {
          borderBottom: `2px solid #30ABA3`,
        },
        '&:after': {
          borderBottom: 'none',
        },
        '&:hover:not($disabled):before': {
          borderBottom: `2px solid #30ABA3`,
        },
        '&$error:not($disabled):before': {
          borderBottom: `1px solid #F45656`,
        },
      },
      disabled: {
        '&:before': {
          borderBottom: `1px solid #C6C6C6`,
        },
      },
    },
    MuiSelect: {
      root: {
        '&:hover:not($disabled)': {
          backgroundColor: '#FFFFFF',
          color: '#525252',
        },
      },
      selected: {
        backgroundColor: '#e7f6f4',
      },
    },
    MuiMenuItem: {
      root: {
        color: '#525252',
        fontSize: 16,
        fontWeight: 400,
        fontStyle: 'normal',
        letterSpacing: 'normal',
        textAlign: 'left',
        '&:hover:not($disabled)': {
          backgroundColor: '#e0e0e0',
        },
        '&$selected': {
          width: 196,
          height: 40,
          backgroundColor: '#e7f6f4',
          color: '#30aba3',
          fontSize: 16,
          fontWeight: 400,
          fontStyle: 'normal',
          letterSpacing: 'normal',
          lineHeight: 22,
          textAlign: 'left',
        },
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
  },
});

const NonUnionRateTable = (props = {}) => {
  const {
    classes: tableClasses = {},
    onChange: upstreamOnChange,
    formData = {},
    formErrors = {},
    isReadOnly = false,
    workScheduleCode,
  } = props;
  const classes = useStyles();
  const {
    overtimeRatePerHour,
    ratePerHour,
    guaranteedHours,
    guaranteedHours6thDay,
    guaranteedHours7thDay,
  } = formData;

  /**
   * Note: Whenever this component is rendered, we are using this key as an
   * indicator to the form object in edit offer
   * state as an indicator to ensure we are showing the right
   * data and to ensure the submit offer goes through.
   * A better solution should be added after the deprecation of termsOfEmploymentV2 key
   * from the backend as it is the primary source of this constraint
   */
  useEffect(() => {
    upstreamOnChange({
      isTermsOfEmploymentV2: true,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isScheduleA = ['A', 'OT', 'CR', 'FS', 'PO', 'XT', 'M'].includes(
    workScheduleCode,
  );
  const isScheduleC = ['C', 'E', 'CN'].includes(workScheduleCode);
  const [isHoursPerDay, setHoursPerDay] = useState(guaranteedHours);
  const [isRatePerHourError, setRateError] = useState(false);
  const [isHoursPerDayWarning, setHoursPerDayWarning] = useState(false);

  const getTypedValue = (ev = {}, formKey) => {
    const { target: { value: rawValue } = {} } = ev;
    let value = rawValue;
    if (rawValue === '' || rawValue === '0') value = '';
    return parseFloat(value);
  };

  const onChangeGuaranteedHours = debounce(ev => {
    const typedValue = getTypedValue(ev);
    if (typedValue > 0) {
      setHoursPerDay(true);
      setHoursPerDayWarning(typedValue > 16);
    } else if (isNaN(typedValue)) {
      setHoursPerDay(false);
      upstreamOnChange({
        guaranteedHours6thDay: false,
        guaranteedHours7thDay: false,
      });
    }
    upstreamOnChange({
      guaranteedHours: typedValue,
    });
  }, 250);

  const onBlur = (ev = {}) => {
    const typedValue = getTypedValue(ev);
    if (typedValue > 0) {
      setRateError(typedValue < 5.5);
      upstreamOnChange({
        ratePerHour: formatCurrency(typedValue),
        overtimeRatePerHour: formatCurrency(typedValue * OVERTIME_MULTIPLIER),
        isTermsOfEmploymentV2: true,
      });
    }
  };

  const onBlurOvertime = ev => {
    const typedValue = getTypedValue(ev);
    upstreamOnChange({
      overtimeRatePerHour: formatCurrency(typedValue),
      isTermsOfEmploymentV2: true,
    });
  };

  const handleDayChange = (ev = {}, radioKey) => {
    const { target: { value: rawValue } = {} } = ev;
    upstreamOnChange({
      [radioKey]: rawValue === 'yes',
    });
  };

  const CustomRadio = (
    <Radio
      classes={{
        root: classes.radio,
        checked: classes.checked,
      }}
    />
  );

  const ScheduleC = () => {
    return <WeeklyRateTable {...props} />;
  };
  const ScheduleA = () => {
    return (
      <>
        <Table>
          <TableRow>
            <TableCell className={classes.cellTitle}>
              <div className={classes.errorContainer}>
                <Typography className={classes.hoursPerDay}>
                  Hourly Rate
                </Typography>
                {(isRatePerHourError || formErrors.ratePerHour) && (
                  <Typography
                    color="warning"
                    variant="caption"
                    className={classes.errorLabel}
                  >
                    <WarningIcon className={classes.errorIcon} />
                    Rate is below minimum wage
                  </Typography>
                )}
              </div>
            </TableCell>
            <TableCell className={classes.rightCellArea}>
              <div className={classes.itemContainer}>
                <Typography className={classes.dollar}>$</Typography>
                <MaskedInput
                  mask={ratePerHourMask}
                  value={ratePerHour}
                  onBlur={onBlur}
                  render={(ref, props) => (
                    <TextField
                      inputRef={ref}
                      {...props}
                      error={isRatePerHourError}
                      disabled={isReadOnly}
                      InputProps={{
                        inputProps: {
                          'data-test-id': 'RatePerHour-input',
                        },
                      }}
                    />
                  )}
                />
                <Typography className={classes.hoursCopy}>/hour</Typography>
              </div>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={classes.cellTitle}>Overtime Rate</TableCell>
            <TableCell className={classes.rightCellArea}>
              <div className={classes.itemContainer}>
                <Typography className={classes.dollar}>$</Typography>
                <MaskedInput
                  mask={ratePerHourMask}
                  value={overtimeRatePerHour || ''}
                  onBlur={onBlurOvertime}
                  render={(ref, props) => (
                    <TextField
                      inputRef={ref}
                      {...props}
                      disabled={isReadOnly}
                      InputProps={{
                        inputProps: {
                          'data-test-id': 'OvertimeRatePerHour-input',
                        },
                      }}
                    />
                  )}
                />
                <Typography className={classes.hoursCopy}>/hour</Typography>
              </div>
            </TableCell>
          </TableRow>
        </Table>
        <Table className={classes.topSpacing}>
          <TableRow>
            <TableCell className={classes.cellTitle} colSpan={2}>
              <div className={classes.errorContainer}>
                <Typography className={classes.hoursPerDay}>
                  Hours per Day
                </Typography>
                {isHoursPerDayWarning && (
                  <Typography
                    color="warning"
                    variant="caption"
                    className={classes.warningLabel}
                  >
                    <WarningIcon className={classes.warningIcon} /> You entered
                    more than 16 hours
                  </Typography>
                )}
              </div>
            </TableCell>
            <TableCell className={classes.rightCellArea}>
              <div className={classes.itemContainer}>
                <div className={classes.dollar}></div>
                <MaskedInput
                  mask={guaranteedHoursMask}
                  value={guaranteedHours || ''}
                  onChange={onChangeGuaranteedHours}
                  render={(ref, props) => (
                    <TextField
                      inputRef={ref}
                      {...props}
                      disabled={isReadOnly}
                      InputProps={{
                        className: isHoursPerDayWarning
                          ? classes.textFieldInputContainerWarning
                          : '',
                        inputProps: {
                          'data-test-id': 'NonUnion-hourPerDay-input',
                        },
                      }}
                    />
                  )}
                />
                <Typography className={classes.hoursCopy}>hours</Typography>
              </div>
            </TableCell>
          </TableRow>
          {isHoursPerDay && (
            <>
              <TableRow>
                <TableCell className={classes.hoursPerDayCell} rowSpan={2}>
                  Apply Hours per Day to
                </TableCell>
                <TableCell className={classes.sixthDayCell} colSpan={1}>
                  6th Day
                </TableCell>
                <TableCell className={classes.radioGroupCell}>
                  <RadioGroup
                    disabled={isReadOnly}
                    value={guaranteedHours6thDay ? 'yes' : 'no'}
                    onChange={ev =>
                      handleDayChange(ev, 'guaranteedHours6thDay')
                    }
                    className={classes.radioBtns}
                  >
                    <FormControlLabel
                      classes={{ label: classes.label }}
                      value="no"
                      control={CustomRadio}
                      label="No"
                    />
                    <FormControlLabel
                      classes={{ label: classes.label }}
                      value="yes"
                      control={CustomRadio}
                      label="Yes"
                    />
                  </RadioGroup>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.cellTitle} colSpan={1}>
                  7th Day
                </TableCell>
                <TableCell className={classes.radioGroupCell}>
                  <RadioGroup
                    disabled={isReadOnly}
                    value={guaranteedHours7thDay ? 'yes' : 'no'}
                    onChange={ev =>
                      handleDayChange(ev, 'guaranteedHours7thDay')
                    }
                    className={classes.radioBtns}
                  >
                    <FormControlLabel
                      classes={{ label: classes.label }}
                      value="no"
                      control={CustomRadio}
                      label="No"
                    />
                    <FormControlLabel
                      classes={{ label: classes.label }}
                      value="yes"
                      control={CustomRadio}
                      label="Yes"
                    />
                  </RadioGroup>
                </TableCell>
              </TableRow>
            </>
          )}
        </Table>
      </>
    );
  };

  return (
    <div className={tableClasses.root}>
      <MuiThemeProvider theme={theme}>
        {isScheduleA && <ScheduleA />}
        {isScheduleC && <ScheduleC />}
      </MuiThemeProvider>
    </div>
  );
};

export default NonUnionRateTable;
